import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, FormGroup, Label,
  Button, Offcanvas, OffcanvasHeader, OffcanvasBody
} from "reactstrap";

import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

/* ===================================================== */

const QuestionMaster = () => {
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchList = () => {
    apiGet("QuestionAnswer/allforagent", { agentId: parseInt(localStorage.getItem("userId")) })
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load questions"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    const payload = {
      ...values,
      agentId: parseInt(localStorage.getItem("userId")),
    };

    apiPost("QuestionAnswer", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    apiPut("QuestionAnswer", values)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`QuestionAnswer/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    question: Yup.string().required("Question required"),
    questionAnswerFor: Yup.number().required(),
    questionType: Yup.number().required(),
    layoutClass: Yup.string().required(),
  });

  const typeNeedsOptions = type => [1, 2].includes(Number(type)); // select / checkbox

  /* ================= RENDER ================= */
const QUESTION_TYPE_LABELS = {
  1: "Select",
  2: "Checkbox",
  6: "Textbox",
  8: "Textarea",
  10: "Currency",
};
  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />

      {/* ================= ADD ================= */}
      <Card>
        <CardBody>
          <h5>Add Question</h5>

          <Formik
            initialValues={{
              questionAnswerFor: 1,
              questionType: 6,
              question: "",
              questionControl: "",
              answersOptions: "",
              answersWeightage: "",
              displayOrder: 0,
              layoutClass: "col-md-6",
              isActive: true,
            }}
            validationSchema={validationSchema}
            onSubmit={handleCreate}
          >
            {({ values }) => (
              <Form>
                <Row>
                  <Col md="3">
                    <Label>For</Label>
                    <SafeField as="select" name="questionAnswerFor" className="form-control">
                      <option value="1">Buyer</option>
                      <option value="2">Seller</option>
                      <option value="3">Investor</option>
                    </SafeField>
                  </Col>

                  <Col md="3">
                    <Label>Type</Label>
                    <SafeField as="select" name="questionType" className="form-control">
                      <option value="1">Select</option>
                      <option value="2">Checkbox</option>
                      <option value="6">Textbox</option>
                      <option value="8">Textarea</option>
                      <option value="10">Currency</option>
                    </SafeField>
                  </Col>

                  <Col md="6">
                    <Label>Question *</Label>
                    <SafeField name="question" className="form-control" />
                    <ErrorMessage name="question" component="div" className="invalid-feedback" />
                  </Col>
                </Row>

                {typeNeedsOptions(values.questionType) && (
                  <Row className="mt-3">
                    <Col md="12">
                      <Label>Answer Options (comma separated)</Label>
                      <SafeField name="answersOptions" className="form-control" />
                    </Col>
                  </Row>
                )}

                <Row className="mt-3">
                  <Col md="3">
                    <Label>Display Order</Label>
                    <SafeField name="displayOrder" type="number" className="form-control" />
                  </Col>

                 <Col md="5">
                        <Label>Layout Width</Label>
                        <SafeField as="select" name="layoutClass" className="form-control">
                            <option value="">Select Width</option>

                            {/* Full width */}
                            <option value="col-12">100% (col-12)</option>

                            {/* Half */}
                            <option value="col-md-6">50% (col-md-6)</option>

                            {/* Third */}
                            <option value="col-md-4">33% (col-md-4)</option>

                            {/* Quarter */}
                            <option value="col-md-3">25% (col-md-3)</option>

                            {/* Two Third */}
                            <option value="col-md-8">66% (col-md-8)</option>

                            {/* Custom responsive */}
                            <option value="col-12 col-md-6">Full → Half</option>
                            <option value="col-12 col-md-4">Full → Third</option>
                            <option value="col-12 col-md-3">Full → Quarter</option>
                        </SafeField>
                        </Col>


                  <Col md="2" className="d-flex align-items-end">
                    <FormGroup check>
                      <Field type="checkbox" name="isActive" /> <Label check>Active</Label>
                    </FormGroup>
                  </Col>

                  <Col md="2" className="d-flex align-items-end">
                    <Button type="submit" color="primary">Save</Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>

      {/* ================= LIST ================= */}
      <Card>
        <CardBody>
          <h5>Questions</h5>

          <table className="table table-bordered">
            <thead>
              <tr>
                <th>For</th>
                <th>Question</th>
                <th>Type</th>
                <th>Order</th>
                <th>Active</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {list.map((q, i) => (
                <tr key={i}>
                  <td>{q.questionAnswerFor === 1 ? "Buyer" : q.questionAnswerFor === 2 ? "Seller" : "Investor"}</td>
                  <td>{q.question}</td>
                  <td>{QUESTION_TYPE_LABELS[q.questionType] || "Unknown"}</td>
                  <td>{q.displayOrder}</td>
                  <td>{q.isActive ? "Yes" : "No"}</td>
                  <td>
                    <ActionEdit onClick={() => openEdit(q)} />{" "}
                    <ActionDelete onClick={() => handleDelete(q.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {/* ================= EDIT ================= */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Question</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ values }) => (
              <Form>
                <FormGroup>
                  <Label>Question</Label>
                  <SafeField name="question" className="form-control" />
                </FormGroup>

                {typeNeedsOptions(values.questionType) && (
                  <FormGroup>
                    <Label>Answer Options</Label>
                    <SafeField name="answersOptions" className="form-control" />
                  </FormGroup>
                )}

                <FormGroup check className="mb-3">
                  <Field type="checkbox" name="isActive" /> <Label check>Active</Label>
                </FormGroup>

                <Button type="submit" color="primary">Update</Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default QuestionMaster;
